import React from 'react';
import { Shield, FileCheck, Search, AlertTriangle, Lock, Eye, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AMLCTFPage = () => {
  const measures = [
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: 'Customer Due Diligence (CDD) / Know Your Customer (KYC)',
      description: 'Verifying the identity of clients and, where applicable, beneficial owners (for example, by requesting identification documents, company registration details, or ownership information).',
      color: 'blue'
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: 'Risk-based Assessment',
      description: 'Assessing the risk profile of clients and transactions, taking into account factors such as country of residence, business activities, and the nature and purpose of the relationship.',
      color: 'green'
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Ongoing Monitoring',
      description: 'Monitoring the business relationship and, where relevant, reviewing information and documentation to ensure it remains accurate and up to date.',
      color: 'purple'
    },
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: 'Sanctions and PEP Screening',
      description: 'Screening clients against relevant sanctions lists and, where appropriate, identifying Politically Exposed Persons (PEPs), in line with applicable laws.',
      color: 'orange'
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'Record-keeping',
      description: 'Retaining certain records and documentation for the legally required periods to comply with AML/CTF and regulatory obligations.',
      color: 'indigo'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Reporting Obligations',
      description: 'Where required by law, reporting unusual or suspicious activities to the competent authorities. In such cases, we may be legally prohibited from informing the client that a report has been made.',
      color: 'red'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-500',
    green: 'bg-green-100 text-green-600 border-green-500',
    purple: 'bg-purple-100 text-purple-600 border-purple-500',
    orange: 'bg-orange-100 text-orange-600 border-orange-500',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-500',
    red: 'bg-red-100 text-red-600 border-red-500'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <Shield className="w-12 h-12 text-white" />
            <h1 className="text-5xl font-bold text-white">
              AML/CTF Compliance
            </h1>
          </div>
          <p className="text-xl text-white/90 max-w-4xl">
            Anti-Money Laundering & Counter-Terrorist Financing
          </p>
          <p className="text-sm text-white/80 mt-4">
            Last Updated: November 2024
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* Introduction */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-[#0d6571] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Commitment to AML/CTF Compliance</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>FXC Management Services B.V. (FXC Management Services)</strong> is committed to complying with 
                  applicable <strong>Anti-Money Laundering (AML)</strong> and <strong>Counter-Terrorist Financing (CTF)</strong> legislation 
                  within the European Union and the Netherlands.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This includes alignment with the principles of the <strong>EU Anti-Money Laundering Directives</strong> (such as AMLD5) 
                  and relevant Dutch implementation laws.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    Although we do not act as a bank, broker, or investment firm, we may be required to apply AML/CTF 
                    measures in connection with our services and business relationships. These measures are designed to 
                    help prevent our services from being misused for money laundering, terrorist financing, or other financial crime.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* AML/CTF Measures */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Our AML/CTF Measures
            </h2>
            <div className="space-y-6">
              {measures.map((measure, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses[measure.color]}`}>
                        {measure.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{measure.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{measure.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why We Ask for Your Documents */}
          <div id="why-documents" className="bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-300 rounded-xl shadow-lg p-8 mb-8 scroll-mt-20">
            <div className="flex items-start gap-4 mb-6">
              <FileCheck className="w-10 h-10 text-yellow-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Why We Ask for Your Documents</h2>
                <p className="text-sm text-gray-600">A simple explanation of our identity verification process</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-3">It's Not Personal – It's Legal</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When we ask for identification documents, it's not because we don't trust you. It's because 
                  <strong> European law requires us</strong> to verify who we're doing business with.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  These rules help protect everyone – including you – from fraud, money laundering, and financial crime.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What We Need & Why</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Identity Document (e.g., Passport, ID Card, Driver's License)</p>
                      <p className="text-gray-700 text-sm mt-1">
                        To confirm you are who you say you are. This is the cornerstone of "Know Your Customer" (KYC) rules.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Proof of Address (e.g., Utility Bill, Bank Statement)</p>
                      <p className="text-gray-700 text-sm mt-1">
                        To verify where you live. This helps us understand your country of residence and assess any 
                        geographic risk factors.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Business Documentation (if applicable)</p>
                      <p className="text-gray-700 text-sm mt-1">
                        Such as company registration or beneficial ownership information. Required when dealing with 
                        business entities to understand who ultimately controls or benefits from the company.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-100 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Data is Protected</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We treat your documents with the highest level of care and confidentiality:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-gray-700">• Your documents are stored securely and encrypted</li>
                  <li className="text-gray-700">• Only authorized personnel have access</li>
                  <li className="text-gray-700">• We comply with GDPR and Dutch data protection laws</li>
                  <li className="text-gray-700">• Documents are retained only as long as legally required</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">What Happens If I Don't Provide Documents?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unfortunately, without proper verification, we cannot establish or continue a business relationship. 
                  This is not optional – it's a legal requirement we must follow. We appreciate your understanding 
                  and cooperation in helping us meet these obligations.
                </p>
              </div>
            </div>
          </div>

          {/* Client Acknowledgment */}
          <div className="bg-gradient-to-br from-red-50 to-white border-2 border-red-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Client Acknowledgment</h2>
                <p className="text-sm text-gray-600">Important information for all clients</p>
              </div>
            </div>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">
                By engaging with us, you acknowledge that:
              </p>
              
              <div className="bg-white rounded-lg p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0 mt-2"></div>
                  <p>
                    We may <strong>request additional information or documentation</strong> to meet our AML/CTF obligations
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0 mt-2"></div>
                  <p>
                    If sufficient information is not provided, or if we identify <strong>unacceptable AML/CTF risk</strong>, 
                    we may be unable to establish or continue a business relationship
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0 mt-2"></div>
                  <p>
                    We may be <strong>legally prohibited from informing you</strong> if we report suspicious activities 
                    to competent authorities
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0 mt-2"></div>
                  <p>
                    Failure to comply with our reasonable requests for information may result in 
                    <strong> suspension or termination</strong> of services
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-[#0d6571] to-[#0d6571]/80 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About AML/CTF Compliance?</h2>
            <p className="text-white/90 mb-6">
              If you have questions about our AML/CTF procedures or need clarification about the documents we require, 
              please don't hesitate to contact us.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-3 bg-white text-[#0d6571] rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
          </div>

          {/* Company Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p className="font-semibold text-gray-900 mb-2">
              FXC Management Services B.V. (FXC Management Services)
            </p>
            <p>
              Registered Address: Industrieweg 9, 2254AE Voorschoten, The Netherlands
            </p>
            <p className="mt-2">
              Chamber of Commerce Registration: 78204992
            </p>
            <p className="mt-4 text-xs">
              Compliance Officer: info@fxcvantage.com
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AMLCTFPage;
